import { useCallback, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Info } from 'lucide-react';

import { cn } from '@/lib/cn';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './alert-dialog';
import { Button } from './button';
import { Input } from './input';
import { SurfaceProvider } from './surface';
import type { Surface } from './surface.context';
import {
  ConfirmContext,
  type ConfirmContextValue,
  type ConfirmOptions,
} from './confirm-dialog.context';

/**
 * Imperative confirmation dialog wired into the design system.
 *
 * Usage:
 *
 *   const confirm = useConfirm();
 *   const ok = await confirm({
 *     title: '¿Eliminar marcador?',
 *     description: 'Esta acción no se puede deshacer.',
 *     confirmLabel: 'Eliminar',
 *     variant: 'destructive',
 *   });
 *   if (ok) deleteMarker(id);
 *
 * Why imperative (not `<ConfirmDialog open={...}>` in every caller): the
 * vast majority of confirmations are one-shot — opened, awaited, resolved.
 * Forcing each caller to thread state + callbacks just to render a stock
 * dialog is boilerplate that obscures the actual business logic. The
 * imperative API keeps the call-site to a single `await`.
 *
 * Surface: light on pages, dark when `confirm()` was called from inside the
 * panorama editor (`useConfirm()` captures the caller's surface).
 *
 * Mount once at the App root — only ONE dialog can be open at a time, which
 * matches the UX expectation anyway (stacked confirms are an antipattern).
 */

interface InternalState extends ConfirmOptions {
  open: boolean;
  surface: Surface;
}

export function ConfirmDialogProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<InternalState>({
    open: false,
    title: '',
    surface: 'light',
  });
  // Keep a ref to the latest `resolve` so the radix onOpenChange handler
  // (which fires when the user hits Esc or clicks outside) can resolve
  // even if state updates race.
  const resolveRef = useRef<((ok: boolean) => void) | null>(null);
  const [typed, setTyped] = useState('');

  const confirm = useCallback((opts: ConfirmOptions, surface: Surface = 'light'): Promise<boolean> => {
    return new Promise<boolean>(resolve => {
      // If a prior dialog is somehow still open, auto-cancel it first so
      // the new one takes over cleanly.
      if (resolveRef.current) {
        resolveRef.current(false);
        resolveRef.current = null;
      }
      resolveRef.current = resolve;
      setTyped('');
      setState({ ...opts, open: true, surface });
    });
  }, []);

  const handleResolve = useCallback((ok: boolean) => {
    const r = resolveRef.current;
    resolveRef.current = null;
    setState(s => ({ ...s, open: false }));
    r?.(ok);
  }, []);

  const ctxValue = useMemo<ConfirmContextValue>(() => ({ confirm }), [confirm]);

  const isDestructive = state.variant === 'destructive';
  const isWarning = state.variant === 'warning';
  const needsText = !!state.requireText;
  const textMatches = !needsText || typed.trim() === state.requireText?.trim();
  const Icon = isDestructive || isWarning ? AlertTriangle : Info;
  const iconTone = isDestructive
    ? 'bg-danger-subtle text-danger'
    : isWarning
      ? 'bg-warning-subtle text-warning'
      : 'bg-brand-subtle text-brand';

  return (
    <ConfirmContext.Provider value={ctxValue}>
      {children}
      <SurfaceProvider value={state.surface}>
        <AlertDialog
          open={state.open}
          onOpenChange={open => {
            // Radix fires this on Esc / outside-click → treat as cancel.
            if (!open) handleResolve(false);
          }}
        >
          <AlertDialogContent>
            <div className="flex items-start gap-3">
              <div
                data-slot="confirm-icon"
                data-tone={isDestructive ? 'danger' : isWarning ? 'warning' : 'neutral'}
                className={cn(
                  'flex size-10 shrink-0 items-center justify-center rounded-pill [&_svg]:size-5',
                  iconTone,
                )}
              >
                <Icon />
              </div>
              <AlertDialogHeader className="min-w-0 flex-1">
                <AlertDialogTitle className="text-base">{state.title}</AlertDialogTitle>
                {state.description && <AlertDialogDescription>{state.description}</AlertDialogDescription>}
              </AlertDialogHeader>
            </div>

            {state.notice && (
              <p className="mt-4 rounded-control border border-warning/30 bg-warning-subtle px-3 py-2 text-sm text-warning">
                {state.notice}
              </p>
            )}

            {needsText && (
              <label className="mt-4 block text-sm text-ink-2">
                Escribe <span className="font-semibold text-ink">{state.requireText}</span> para confirmar
                <Input
                  type="text"
                  autoFocus
                  autoComplete="off"
                  spellCheck={false}
                  value={typed}
                  onChange={e => setTyped(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && textMatches) handleResolve(true);
                  }}
                  className="mt-1.5"
                />
              </label>
            )}

            <AlertDialogFooter>
              <AlertDialogCancel asChild>
                <Button
                  variant="secondary"
                  autoFocus={isDestructive && !needsText}
                  onClick={() => handleResolve(false)}
                >
                  {state.cancelLabel ?? 'Cancelar'}
                </Button>
              </AlertDialogCancel>
              <AlertDialogAction asChild>
                <Button
                  variant={isDestructive ? 'danger' : 'primary'}
                  autoFocus={!needsText && !isDestructive}
                  disabled={!textMatches}
                  onClick={e => {
                    // Radix's Action closes the dialog on click even when the
                    // button is disabled via a keyboard path; guard here too.
                    if (!textMatches) {
                      e.preventDefault();
                      return;
                    }
                    handleResolve(true);
                  }}
                >
                  {state.confirmLabel ?? 'Confirmar'}
                </Button>
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </SurfaceProvider>
    </ConfirmContext.Provider>
  );
}
